import { CommonModule } from "@angular/common";
import { afterNextRender, Component, inject, signal } from "@angular/core";
import { Router, RouterModule } from "@angular/router";
import {
    LayoutStore,
    LocalStorageService,
    ModalService,
} from "@malirang/front-share";
import { injectNavigationEnd } from "ngxtension/navigation-end";

import { rxResource } from "@angular/core/rxjs-interop";
import { Capacitor } from "@capacitor/core";
import { Preferences } from "@capacitor/preferences";
import {
    PopupDto,
    PopupService,
    SearchResponsePopupDto,
} from "@malirang/api-client";
import {
    DeviceType,
    fnGetKeyByValue,
    MD_WIDTH_SIZE,
    MemberRole,
    NO_SEE_TODAY_ADVERTISE_POPUP,
    NO_SEE_TODAY_NORMAL_POPUP,
    PopupStatus,
    PopupType,
} from "@malirang/common";
import dayjs from "dayjs";
import { toObservableSignal } from "ngxtension/to-observable-signal";
import { ActionSheetModalService } from "../../services/action-sheet-modal.service";
import { ChildStore } from "../../stores/child.store";
import PopupMobileComponent from "../dashboard/components/popup-mobile/popup-mobile.component";
import PopupComponent from "../dashboard/components/popup/popup.component";
import FooterComponent from "./footer/footer.component";
import HeaderComponent from "./header/header.component";

@Component({
    selector: "app-layout",
    standalone: true,
    imports: [CommonModule, FooterComponent, HeaderComponent, RouterModule],
    templateUrl: "./layout.page.html",
    styleUrl: "./layout.page.scss",
})
export default class LayoutPage {
    readonly navigationEnd$ = injectNavigationEnd();
    readonly router = inject(Router);
    readonly layoutStore = inject(LayoutStore);
    readonly localStorageService = inject(LocalStorageService);
    readonly popupService = inject(PopupService);
    readonly childStore = inject(ChildStore);
    readonly actionSheetModalService = inject(ActionSheetModalService);
    readonly modalService = inject(ModalService);

    path = signal<string>("");

    constructor() {
        afterNextRender(() => {
            this.path.set(this.router.url);
            this.navigationEnd$.subscribe((event) => {
                this.path.set(event.url);
            });

            this.res.subscribe(async (response: any) => {
                const res = response as SearchResponsePopupDto;

                if (res) {
                    // 내 아동 조회
                    this.childStore.fetch();

                    const adPopups = res.items.filter(
                        (item: PopupDto) =>
                            item.type ===
                            fnGetKeyByValue(PopupType, PopupType.ADVERTISE),
                    );

                    const normalPopups = res.items.filter(
                        (item: PopupDto) =>
                            item.type ===
                            fnGetKeyByValue(PopupType, PopupType.NORMAL),
                    );

                    // 팝업 열기

                    setTimeout(() => {
                        this.handleOpenPopups(adPopups, normalPopups);
                    }, 300);
                }
            });
        });
    }

    $res = rxResource({
        params: () => ({
            pageNo: 1,
            pageSize: 9999,
            status: fnGetKeyByValue(PopupStatus, PopupStatus.SHOWING),
            targetDevices: Capacitor.isNativePlatform()
                ? [fnGetKeyByValue(DeviceType, DeviceType.MOBILE)]
                : [fnGetKeyByValue(DeviceType, DeviceType.PC)],
            targetRoles: [fnGetKeyByValue(MemberRole, MemberRole.PARENT)],
        }),
        stream: ({ params }) =>
            this.popupService.popupControllerSearch({
                ...params,
            }),
    });
    res = toObservableSignal(this.$res.value);

    /**
     * @name movePage
     * @description 내 아동 페이지로 이동
     * @returns {void}
     */
    movePage(): void {
        // 임시 주석
        // if (this.childStore?.children()?.length) {
        //   this.router.navigate(['/my-child/dashboard'], {
        //     replaceUrl: true,
        //   });
        // }
    }

    /**
     * @name handleOpenPopups
     * @description 팝업 열기
     * @returns {void}
     */
    async handleOpenPopups(
        adPopups: PopupDto[],
        normalPopups: PopupDto[],
    ): Promise<void> {
        const ad =
            (
                await Preferences?.get({
                    key: NO_SEE_TODAY_ADVERTISE_POPUP,
                })
            )?.value ?? null;

        if (ad && dayjs(ad)?.isValid() && dayjs()?.isBefore(dayjs(ad))) {
            adPopups = [];
        }

        const normal =
            (await Preferences?.get({ key: NO_SEE_TODAY_NORMAL_POPUP }))
                ?.value ?? null;

        if (
            normal &&
            dayjs(normal).isValid() &&
            dayjs().isBefore(dayjs(normal))
        ) {
            normalPopups = [];
        }

        const isAdPopupOpen = adPopups && adPopups.length > 0;
        const isNormalPopupOpen = normalPopups && normalPopups.length > 0;

        if (!isAdPopupOpen && !isNormalPopupOpen) {
            return this.movePage();
        }

        if (Capacitor.isNativePlatform()) {
            if (isAdPopupOpen) {
                const adModalForMobile = this.actionSheetModalService.create(
                    PopupMobileComponent,
                    {
                        componentProps: { popups: adPopups },
                    },
                );

                if (isNormalPopupOpen) {
                    adModalForMobile?.closed?.subscribe(() => {
                        this.actionSheetModalService.create(
                            PopupMobileComponent,
                            {
                                componentProps: { popups: normalPopups },
                            },
                        );
                    });
                }
            } else if (isNormalPopupOpen) {
                this.actionSheetModalService.create(PopupMobileComponent, {
                    componentProps: { popups: normalPopups },
                });
            }
        } else {
            if (window.innerWidth < MD_WIDTH_SIZE) {
                if (isAdPopupOpen) {
                    const adModalForMobile =
                        this.actionSheetModalService.create(
                            PopupMobileComponent,
                            {
                                componentProps: { popups: adPopups },
                            },
                        );

                    if (isNormalPopupOpen) {
                        adModalForMobile?.closed?.subscribe(() => {
                            this.actionSheetModalService.create(
                                PopupMobileComponent,
                                {
                                    componentProps: { popups: normalPopups },
                                },
                            );
                        });
                    }
                } else if (isNormalPopupOpen) {
                    this.actionSheetModalService.create(PopupMobileComponent, {
                        componentProps: { popups: normalPopups },
                    });
                }
            } else {
                const modal = this.modalService.create(PopupComponent, {
                    componentProps: {
                        adPopups,
                        normalPopups,
                    },
                });

                modal?.closed?.subscribe(() => {
                    this.movePage();
                });
            }
        }
    }
}
